// src/components/moderation/ConversationSafetyModal.tsx
import React, { useState } from 'react';
import { Text } from 'react-native';
import { AppModal, ModalAction } from '@/components/modal';
import { useThemeColors } from '@/lib/useThemeColors';
import { handleError } from '@/lib/errors';
import {
  usePairBlock,
  useBlockPair,
  useUnblockPair,
  type BlockSide,
  type Pair,
} from '@/lib/moderation/api';

interface ConversationSafetyModalProps {
  visible: boolean;
  onClose: () => void;
  pair: Pair;
  side: BlockSide;
  counterpartyName: string;
  /**
   * RealtimeChat owns the single ReportContentModal (it already needs one for
   * message long-press). This modal must NEVER open its own — iOS cannot
   * reliably present a second RN `Modal` over one that's still visible — so it
   * closes itself and hands control back via this callback instead.
   */
  onReportConversation: () => void;
}

/**
 * The conversation's "More" menu: report the whole conversation, or block/
 * unblock the counterparty. A block is between the two PARTIES (startup ↔
 * investor profile), not two users, so any active member of either side sees
 * the same block state and either side's members can lift their own side's
 * block — see usePairBlock's `blockedByMySide`.
 */
export function ConversationSafetyModal({
  visible,
  onClose,
  pair,
  side,
  counterpartyName,
  onReportConversation,
}: ConversationSafetyModalProps) {
  const colors = useThemeColors();
  const { block } = usePairBlock(pair, side);
  const blockPair = useBlockPair();
  const unblockPair = useUnblockPair();
  const [error, setError] = useState<string | null>(null);

  // AppModal does not unmount its children on close, so local state (this
  // `error` included) survives a close/reopen unless explicitly cleared here.
  const close = () => {
    setError(null);
    onClose();
  };

  const handleBlock = async () => {
    setError(null);
    try {
      await blockPair.mutateAsync({ ...pair, side });
      close();
    } catch (err) {
      const { message, isCancelled } = handleError(err, { context: 'ConversationSafetyModal.block' });
      if (!isCancelled) setError(message);
    }
  };

  const handleUnblock = async () => {
    setError(null);
    try {
      await unblockPair.mutateAsync(pair);
      close();
    } catch (err) {
      const { message, isCancelled } = handleError(err, { context: 'ConversationSafetyModal.unblock' });
      if (!isCancelled) setError(message);
    }
  };

  return (
    <AppModal
      visible={visible}
      onClose={close}
      title="Conversation options"
      footer={
        <AppModal.Actions>
          <ModalAction
            label="Report conversation"
            variant="neutral"
            onPress={() => {
              close();
              onReportConversation();
            }}
          />
          {block?.blockedByMySide ? (
            <ModalAction
              label={`Unblock ${counterpartyName}`}
              variant="primary"
              onPress={handleUnblock}
              disabled={unblockPair.isPending}
            />
          ) : (
            <ModalAction
              label={`Block ${counterpartyName}`}
              variant="destructive"
              onPress={handleBlock}
              disabled={blockPair.isPending || !!block}
            />
          )}
        </AppModal.Actions>
      }
    >
      <Text style={{ color: colors.text.secondary }}>
        Blocking stops all messages between you and {counterpartyName}, both ways. You can unblock at any time.
      </Text>
      {error ? (
        <Text numberOfLines={2} style={{ color: colors.error, marginTop: 10 }}>
          {error}
        </Text>
      ) : null}
    </AppModal>
  );
}
